/**
 * The web terminal of a project (vfs-service, docs: the terminal contract):
 * - `GET /api/v1/projects/:id/terminal` (the sandbox gateway, or the pod's vfs-service without one): may this person
 *   open a terminal here -> `{ available, reason, message, role, owner: { sub, name, email }, ownSandbox, maxSessions,
 *   idleMinutes, targets? }`; `targets` (newer backends): `{ workspace, runner, vfs }`, each `{ available, reason,
 *   message }`, where a shell may run (TERMINAL_TARGETS). A 404/403 (an older backend, a project the person cannot see) or a network error is null: no tab.
 * - `WS /api/v1/projects/:id/terminal?cols=&rows=[&target=runner|vfs]`: the shell itself, spoken by
 *   utils/terminalClient.js. The system containers (runner, vfs) are for the sandbox owner with the plan's
 *   "Terminal in the system containers"; the server refuses them otherwise.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const base = () => (config.vfsServiceUrl || '').replace(/\/+$/, '');

/** What the terminal endpoint says for this project, or null when it cannot be asked or answers with an error. */
export async function getTerminalInfo(projectId) {
  if (!base() || !projectId) { return null; }
  try {
    const response = await getAuthorizedFetch()(`${base()}/api/v1/projects/${encodeURIComponent(projectId)}/terminal`, { headers: { Accept: 'application/json' } });
    if (!response.ok) { return null; }
    const data = await response.json().catch(() => null);
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/**
 * Whether the Terminal tab shows: never for a viewer, nor when the owner's plan has it off, nor without an answer.
 * Another reason (`disabled`) still shows the tab, with the server's message instead of a shell.
 */
export function canShowTerminal(info) {
  if (!info) { return false; }
  if (info.role === 'viewer') { return false; }
  if (info.available) { return true; }
  return info.reason !== 'plan' && info.reason !== 'role';
}

/** "Runs in Ada Lovelace's sandbox" when the shell runs in someone else's sandbox (an editor's), else null. */
export function sandboxOwnerLabel(info) {
  if (!info || info.ownSandbox !== false) { return null; }
  const who = info.owner?.name || info.owner?.email;
  return who ? `Runs in ${who}'s sandbox` : 'Runs in the owner\'s sandbox';
}

/** Where a shell can run: the project's folder (today's terminal) and the sandbox's two system containers. */
export const TERMINAL_TARGETS = [
  { id: 'workspace', label: 'Workspace', desc: 'The project\'s folder, with your tools' },
  { id: 'runner', label: 'Preview runner', desc: 'The container that runs your services' },
  { id: 'vfs', label: 'vfs-service', desc: 'The container that keeps your files' },
];

/**
 * Whether the shell can run on `target` for this person (`{ available, message }`): the workspace whenever the
 * terminal is available; the others only when the server's `targets` say so (an older backend: not offered).
 */
export function terminalTarget(info, target) {
  if (!info) { return { available: false, message: null }; }
  if (target === 'workspace') { return { available: Boolean(info.available), message: info.available ? null : info.message || null }; }
  const known = info.targets?.[target];
  if (!known) { return { available: false, message: 'Not offered by this sandbox.' }; }
  return { available: Boolean(known.available), message: known.available ? null : known.message || null };
}

/** The terminal socket's URL (same rule as presenceUrl: a relative base is on this origin, http -> ws), or null. */
export function terminalUrl(projectId, { cols, rows, target } = {}, vfsBase = config.vfsServiceUrl) {
  if (!vfsBase || !projectId) { return null; }
  const absolute = vfsBase.startsWith('/') && typeof window !== 'undefined' ? `${window.location.origin}${vfsBase}` : vfsBase;
  const query = new URLSearchParams();
  if (cols) { query.set('cols', String(cols)); }
  if (rows) { query.set('rows', String(rows)); }
  if (target && target !== 'workspace') { query.set('target', target); }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return `${absolute.replace(/\/+$/, '').replace(/^http/i, 'ws')}/api/v1/projects/${encodeURIComponent(projectId)}/terminal${suffix}`;
}
